import React, { useMemo, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Button, ButtonProps } from '@shared/components/common/Button';
import { cn } from '@shared/utils/utils';
import { CreditCard, BookOpen, ChevronDown, UserCircle, LucideIcon } from 'lucide-react';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger
} from '@shared/components/common/Collapsible';
import { useStore } from '@shared/hooks/useStore';
import { ROUTES } from '@shared/configs/constants';
import { SYNCHRONOUS_ORIGIN } from '@/shared/types/enums';

const ProfileLayout = () => {
    return (
        <div className='flex flex-col bg-gradient-to-br from-gray-50 to-gray-100'>
            <div className='flex-1 container mx-auto p-4 pb-10 md:px-6 md:pt-8 md:pb-20 grid lg:grid-cols-[320px_1fr] gap-6 lg:gap-10'>
                {/* Sidebar */}
                <aside className='hidden sticky top-40 h-fit lg:block'>
                    <ProfileSidebar />
                </aside>

                {/* Main Content */}
                <main className='w-full min-h-[calc(100vh-200px)]'>
                    <Outlet />
                </main>
            </div>
        </div>
    );
};

export default ProfileLayout;

interface MenuItemProps extends ButtonProps {
    to?: string,
    icon?: LucideIcon,
    label: string,
    isActive?: boolean,
    children?: React.ReactNode
}

const MenuItem = ({ to, icon: Icon, label, isActive = false, children, ...props }: MenuItemProps) => {
    const baseClassName = 'w-full justify-start h-14 text-left transition-all duration-200 rounded-lg hover:bg-primary-light hover:text-white';
    const activeClassName = isActive ? 'bg-primary-light text-white' : '';

    const buttonContent = (
        <span className={cn('flex gap-3 items-center')}>
            {Icon && <Icon size={24} />}
            <span className='text-base font-medium'>{label}</span>
            {children}
        </span>
    );
    if (to) {
        return (
            <Link to={to}>
                <Button
                    variant={isActive ? 'default' : 'ghost'}
                    className={cn(baseClassName, activeClassName, props.className)}

                    {...props}
                >
                    {buttonContent}
                </Button>
            </Link>
        );
    }

    return (
        <Button
            variant={isActive ? 'default' : 'ghost'}
            className={cn(baseClassName, activeClassName, props.className)}
            {...props}
        >
            {buttonContent}
        </Button>
    );
};

const ProfileSidebar = () => {
    const { pathname } = useLocation();
    const { authStore: { isLoggedIn, isStdIndividual, profile } } = useStore();
    const [isLibraryOpen, setIsLibraryOpen] = useState(true);

    const librarySubmenus = [
        {
            label: isStdIndividual ? '購入済み書籍・雑誌' : '指定書籍・雑誌',
            to: ROUTES.myLibraryPublications.href
        },
        {
            label: '読書履歴',
            to: ROUTES.myLibraryReadingHistory.href
        },
        {
            label: 'お気に入り',
            to: ROUTES.myLibraryFavorites.href
        }
    ];

    const isLibraryActive = librarySubmenus.some(
        submenu => pathname === submenu.to || pathname.startsWith(submenu.to + '/')
    );

    const isTargetService = useMemo(() => {
        if (!isLoggedIn) return true;
        if (!profile?.synchronousOrigin) return false;
        return ![SYNCHRONOUS_ORIGIN.EE, SYNCHRONOUS_ORIGIN.EI].includes(profile.synchronousOrigin);
    }, [profile?.synchronousOrigin, isLoggedIn]);

    return (
        <nav className='p-6 bg-white rounded-lg shadow-lg'>
            <h1 className='mb-8 text-3xl font-semibold'>
                マイページ
            </h1>
            <div className='flex flex-col gap-3'>
                {/* アカウント情報 */}

                {isTargetService &&
                    (
                        <MenuItem
                            to='/profile'
                            icon={UserCircle}
                            label='アカウント情報'
                            isActive={pathname.startsWith('/profile/edit') || pathname === '/profile'}
                        />
                    )}

                {/* 購入履歴 */}

                {isStdIndividual && (
                    <MenuItem
                        to='/profile/purchase-history'
                        icon={CreditCard}
                        label='購入履歴'
                        isActive={pathname.startsWith('/profile/purchase-history')}
                    />
                )}

                {/* マイ本棚 with submenu */}
                <Collapsible
                    open={isLibraryOpen}
                    onOpenChange={setIsLibraryOpen}
                    className='w-full'
                >
                    <CollapsibleTrigger className='w-full h-14 cursor-pointer' asChild>
                        <MenuItem
                            icon={BookOpen}
                            label='マイ本棚'
                            isActive={isLibraryActive}
                            className='w-full'
                            asChild
                        >
                            <ChevronDown
                                size={20}
                                className={cn(
                                    'transition-transform duration-200 ml-auto',
                                    isLibraryOpen && 'rotate-180'
                                )}
                            />
                        </MenuItem>
                    </CollapsibleTrigger>
                    <CollapsibleContent className='overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down'>
                        <div className='flex flex-col gap-3 mt-3 ml-4'>
                            {librarySubmenus.map((submenu) => {
                                const isSubmenuActive = pathname === submenu.to || pathname.startsWith(submenu.to + '/');
                                return (
                                    <MenuItem
                                        key={submenu.to}
                                        to={submenu.to}
                                        label={submenu.label}
                                        isActive={isSubmenuActive}
                                    />
                                );
                            })}
                        </div>
                    </CollapsibleContent>
                </Collapsible>
            </div>
        </nav>
    );
};
